import { cn } from "../../lib/utils"
import { Label } from "./label"
import { Separator } from "./separator"

const fieldStyles = {
  set: "flex flex-col gap-4",
  legend:
    "mb-1.5 font-medium data-[variant=label]:text-sm data-[variant=label]:leading-sm data-[variant=legend]:text-md data-[variant=legend]:leading-md",
  group: "flex w-full flex-col gap-5 data-[slot=checkbox-group]:gap-3",
  root:
    "flex w-full gap-2 data-[invalid=true]:text-destructive data-[orientation=vertical]:flex-col data-[orientation=vertical]:[&>.sr-only]:hidden data-[orientation=horizontal]:flex-row data-[orientation=horizontal]:items-center data-[orientation=horizontal]:justify-between data-[orientation=responsive]:flex-col data-[orientation=responsive]:[&>.sr-only]:hidden",
  content: "flex flex-1 flex-col gap-0.5 leading-snug",
  label:
    "flex w-fit gap-2 leading-snug [data-slot=field][data-disabled=true]_&:opacity-50 has-[[data-slot=field]]:flex-col",
  title:
    "flex w-fit items-center gap-2 text-sm font-medium leading-sm [data-slot=field][data-disabled=true]_&:opacity-50",
  description:
    "text-left text-sm font-normal leading-sm text-muted-foreground [data-variant=legend]+&:-mt-1.5 last:mt-0 [&:nth-last-child(2)]:-mt-1 [&>a]:underline-offset-4 [&>a:hover]:text-primary",
  separator:
    "relative -my-2 h-5 text-sm leading-sm [data-slot=field-group][data-variant=outline]_&:-mb-2",
  separatorLine: "absolute inset-0",
  separatorContent:
    "relative mx-auto block w-fit bg-bg px-2 text-muted-foreground",
  error: "text-sm font-normal leading-sm text-destructive",
  errorList: "ml-4 flex flex-col gap-1",
}

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(fieldStyles.set, className)}
      {...props}
    />
  )
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(fieldStyles.legend, className)}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(fieldStyles.group, className)}
      {...props}
    />
  )
}

type FieldVariantProps = {
  orientation?: "vertical" | "horizontal" | "responsive"
}

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & FieldVariantProps) {
  return (
    <div
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldStyles.root, className)}
      {...props}
    />
  )
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(fieldStyles.content, className)}
      {...props}
    />
  )
}

function FieldLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(fieldStyles.label, className)}
      {...props}
    />
  )
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(fieldStyles.title, className)}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(fieldStyles.description, className)}
      {...props}
    />
  )
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(fieldStyles.separator, className)}
      {...props}
    >
      <Separator className={fieldStyles.separatorLine} />
      {children && (
        <span
          className={fieldStyles.separatorContent}
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  let content = children
  if (!content && errors?.length) {
    const uniqueMessages = [
      ...new Set(
        errors
          .map((error) => error?.message)
          .filter((message): message is string => Boolean(message))
      ),
    ]

    if (uniqueMessages.length == 1) {
      content = uniqueMessages[0]
    } else {
      content = (
        <ul className={fieldStyles.errorList}>
          {uniqueMessages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )
    }
  }

  if (!content) {
    return null
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(fieldStyles.error, className)}
      {...props}
    >
      {content}
    </div>
  )
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
}
